import type {
  StrategyEntry,
  StrategyUpdateEntry,
} from "../services/investorStrategyService";
import type { ProductLookupKey } from "./strategyUtils";

type StrategyIdProductMapLike = Map<number, { entityType: string; productId: number }>;

export type ProductAddedDateMap = Map<ProductLookupKey, string | null>;

function isStrategyEntityType(value: unknown): value is "PORTFOLIO" | "BASKET" {
  return value === "PORTFOLIO" || value === "BASKET";
}

function parseProductId(value: unknown): number | null {
  if (typeof value === "number" && Number.isInteger(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isInteger(parsed)) return parsed;
  }
  return null;
}

function resolveHistoryProduct(
  entry: StrategyUpdateEntry,
  strategyIdProductMap: StrategyIdProductMapLike
): ProductLookupKey | null {
  const payloadEntityType = entry.payload.entityType;
  const payloadProductId = parseProductId(entry.payload.productId);

  if (isStrategyEntityType(payloadEntityType) && payloadProductId !== null) {
    return `${payloadEntityType}-${payloadProductId}`;
  }

  const fallback = strategyIdProductMap.get(entry.investorStrategyId);
  if (!fallback || !isStrategyEntityType(fallback.entityType)) return null;

  return `${fallback.entityType}-${fallback.productId}`;
}

function getTimeMs(datetimeUtc: string): number {
  const ms = new Date(datetimeUtc).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export function computeProductAddedDates(
  strategies: Pick<StrategyEntry, "entityType" | "productId">[],
  allHistoryItems: StrategyUpdateEntry[],
  strategyIdProductMap: StrategyIdProductMapLike
): ProductAddedDateMap {
  const currentKeys = new Set<ProductLookupKey>();
  const addedDates: ProductAddedDateMap = new Map();

  for (const strategy of strategies) {
    const key: ProductLookupKey = `${strategy.entityType}-${strategy.productId}`;
    currentKeys.add(key);
    addedDates.set(key, null);
  }

  if (currentKeys.size === 0 || allHistoryItems.length === 0) {
    return addedDates;
  }

  const resolved = allHistoryItems
    .map((entry) => ({
      entry,
      key: resolveHistoryProduct(entry, strategyIdProductMap),
    }))
    .filter((item): item is { entry: StrategyUpdateEntry; key: ProductLookupKey } =>
      item.key !== null && currentKeys.has(item.key)
    )
    .sort((a, b) => {
      const timeDiff = getTimeMs(a.entry.datetimeUtc) - getTimeMs(b.entry.datetimeUtc);
      if (timeDiff !== 0) return timeDiff;
      return a.entry.id - b.entry.id;
    });

  for (const { entry, key } of resolved) {
    if (entry.method === "POST") {
      addedDates.set(key, entry.datetimeUtc);
    } else if (entry.method === "DELETE") {
      addedDates.set(key, null);
    }
  }

  return addedDates;
}
